/**
 * IdentityAside — read-only summary panel pinned to the left of a
 * settings form (profile edit, change password, etc.). Renders the
 * user's initials, name, role, and contact info as a "you are here"
 * anchor while the form on the right mutates those same values.
 *
 * Layout: gradient banner strip at the top, 88×88 avatar overlapping
 * the banner, name + role line, then labelled rows for email and
 * phone. The component is sticky-positioned by the consuming page
 * (set `top` via the page's grid wrapper).
 *
 *   <IdentityAside user={user} role="Care Provider · QHSLAB" />
 *   <IdentityAside user={user} hipaaNote="Profile data is HIPAA-…" />
 */
import { styled } from '../styled';
import { formatPhoneForDisplay } from '@shared/lib/phone';

const Aside = styled('aside')({
  position: 'sticky',
  top: 24,
  background: 'var(--qhs-surface)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 12,
  padding: 24,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(0, 0, 0, 0.04)',
});

const Banner = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  height: 78,
  background:
    'linear-gradient(135deg, var(--qhs-midnight) 0%, var(--qhs-deep) 60%, var(--qhs-brand) 100%)',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.06 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
    opacity: 0.5,
  },
});

const Body = styled('div')({
  position: 'relative',
  paddingTop: 38,
  textAlign: 'center',
});

const AvatarXL = styled('div')({
  width: 88,
  height: 88,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #ffffff, #e8eef6)',
  color: 'var(--qhs-brand)',
  fontFamily: "'Lato', sans-serif",
  fontWeight: 900,
  fontSize: 30,
  letterSpacing: '-0.5px',
  display: 'grid',
  placeItems: 'center',
  margin: '0 auto 14px',
  border: '4px solid var(--qhs-surface)',
  boxShadow: '0 8px 24px rgba(10, 20, 64, 0.15)',
  position: 'relative',
});

const Status = styled('span')({
  position: 'absolute',
  right: 4,
  bottom: 4,
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: 'var(--qhs-success)',
  border: '3px solid var(--qhs-surface)',
});

const Name = styled('div')({
  fontFamily: "'Lato', sans-serif",
  fontSize: 18,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  letterSpacing: '-0.2px',
});

const Role = styled('div')({
  fontSize: 12,
  color: 'var(--qhs-text-3)',
  marginTop: 3,
});

const Rule = styled('div')({
  height: 1,
  background: 'var(--qhs-border)',
  margin: '18px -24px',
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 0',
  textAlign: 'left',
});

const RowIcon = styled('span')({
  width: 14,
  height: 14,
  color: 'var(--qhs-text-3)',
  flexShrink: 0,
  marginTop: 2,
  display: 'inline-flex',
  '& svg': { width: 14, height: 14 },
});

const RowMeta = styled('div')({
  flex: 1,
  minWidth: 0,
});

const RowLabel = styled('div')({
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--qhs-text-3)',
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  marginBottom: 2,
});

const RowValue = styled('div')({
  fontSize: 13,
  color: 'var(--qhs-text-1)',
  fontWeight: 500,
  wordBreak: 'break-word',
});

const Tip = styled('div')({
  marginTop: 14,
  padding: '10px 12px',
  background: 'var(--qhs-bg)',
  borderRadius: 8,
  fontSize: 11.5,
  color: 'var(--qhs-text-2)',
  lineHeight: 1.5,
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  textAlign: 'left',
  '& svg': {
    width: 13,
    height: 13,
    color: 'var(--qhs-brand)',
    flexShrink: 0,
    marginTop: 2,
  },
});

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const initialsOf = (firstName, lastName, email) => {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f || l) return `${f[0] || ''}${l[0] || ''}`.toUpperCase() || '—';
  const e = (email || '').trim();
  if (e) return e.slice(0, 2).toUpperCase();
  return '—';
};

const fullName = (firstName, lastName) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const IdentityAside = ({ user, role, hipaaNote }) => {
  const initials = initialsOf(user?.firstName, user?.lastName, user?.email);
  const name = fullName(user?.firstName, user?.lastName) || user?.email || '—';

  return (
    <Aside>
      <Banner />
      <Body>
        <AvatarXL>
          {initials}
          <Status title="Active" />
        </AvatarXL>
        <Name>{name}</Name>
        {role && <Role>{role}</Role>}
        <Rule />
        {user?.email && (
          <Row>
            <RowIcon>
              <MailIcon />
            </RowIcon>
            <RowMeta>
              <RowLabel>Email</RowLabel>
              <RowValue>{user.email}</RowValue>
            </RowMeta>
          </Row>
        )}
        {user?.phone && (
          <Row>
            <RowIcon>
              <PhoneIcon />
            </RowIcon>
            <RowMeta>
              <RowLabel>Phone</RowLabel>
              <RowValue>{formatPhoneForDisplay(user.phone)}</RowValue>
            </RowMeta>
          </Row>
        )}
        {hipaaNote && (
          <Tip>
            <ShieldIcon />
            <span>{hipaaNote}</span>
          </Tip>
        )}
      </Body>
    </Aside>
  );
};

export default IdentityAside;
